import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { formatDistanceToNow } from 'date-fns';
import { ClipboardList, RefreshCw } from 'lucide-react';
import { Select } from '@/components/Select';
import { LoadErrorState } from '@/components/ListStates';
import { SettingsCard, SettingsPage, SettingsSection, Switch } from '@/components/settings/SettingsPage';
import { leadAdFormsService, LeadAdForm } from '@/services/lead-ad-forms.service';
import { templatesService, Template } from '@/services/templates.service';
import { useActiveAccount } from '@/hooks/useActiveAccount';

function relativeTime(ts?: string) {
  if (!ts) return null;
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
}

/** A reply means the axios interceptor already toasted the reason. */
const hasNoReply = (e: unknown) => !(e as { response?: unknown })?.response;

function Step({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <li className="flex gap-3">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-2xs font-bold text-primary">{n}</span>
      <div className="min-w-0 flex-1 text-sm leading-relaxed text-text-secondary">{children}</div>
    </li>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

interface FormRowProps {
  form: LeadAdForm;
  approvedTemplates: Template[];
  onUpdated: (updated: LeadAdForm) => void;
}

function FormRow({ form, approvedTemplates, onUpdated }: FormRowProps) {
  const [saving, setSaving] = useState(false);
  const [refetching, setRefetching] = useState(false);

  const toggleAutoGreet = async () => {
    setSaving(true);
    try {
      const updated = await leadAdFormsService.update(form.formUid, {
        autoGreetEnabled: !form.autoGreetEnabled,
      });
      onUpdated(updated);
    } catch (e) {
      if (hasNoReply(e)) toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleTemplateChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSaving(true);
    try {
      const updated = await leadAdFormsService.update(form.formUid, {
        // '' clears the template: an undefined value is dropped from the JSON body, so the server never saw "None".
        greetingTemplateUid: e.target.value,
      });
      onUpdated(updated);
      toast.success('Greeting template saved');
    } catch (err) {
      if (hasNoReply(err)) toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleRefetch = async () => {
    setRefetching(true);
    try {
      const updated = await leadAdFormsService.refetch(form.formUid);
      onUpdated(updated);
      toast.success('Form refreshed from Meta');
    } catch (e) {
      if (hasNoReply(e)) toast.error('Failed to refetch form');
    } finally {
      setRefetching(false);
    }
  };

  const fetched = relativeTime(form.lastFetchedAt);
  const savedUid = form.greetingTemplateUid ?? '';
  // The list only holds approved templates for the active number; keep the saved choice visible rather than showing "None".
  const savedMissing = savedUid !== '' && !approvedTemplates.some((t) => t.templateUid === savedUid);
  const selectId = `greeting-template-${form.formUid}`;

  return (
    <li className="group px-5 py-4 transition-colors hover:bg-surface-alt/40">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-soft text-primary">
          <ClipboardList size={17} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="min-w-0 truncate text-sm font-semibold text-text-primary" title={form.name}>{form.name}</span>
            <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">
              {form.questions.length} question{form.questions.length !== 1 ? 's' : ''}
            </span>
            {form.locale && (
              <span className="rounded-full bg-surface-alt px-2 py-0.5 font-mono text-2xs uppercase text-text-muted">{form.locale}</span>
            )}
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-text-muted">
            <span className="break-all font-mono">{form.metaFormId}</span>
            <span aria-hidden>·</span>
            <span>{fetched ? `Refreshed ${fetched}` : 'Never refreshed'}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={handleRefetch}
          disabled={refetching}
          aria-label={`Refresh ${form.name} from Meta`}
          title="Refresh from Meta"
          className={clsx(
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:cursor-not-allowed',
            !refetching && 'md:opacity-0 md:transition-opacity md:group-hover:opacity-100 md:group-focus-within:opacity-100',
          )}
        >
          <RefreshCw size={14} className={refetching ? 'animate-spin' : undefined} />
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 rounded-xl border border-border bg-surface-alt/50 p-3.5 @md:ml-14">
        <div className="flex min-w-0 items-center gap-3">
          <Switch
            checked={form.autoGreetEnabled}
            onChange={() => void toggleAutoGreet()}
            disabled={saving}
            label={`Auto-greet new leads from ${form.name}`}
          />
          <div className="min-w-0">
            <div className="text-sm font-medium text-text-primary">Auto-greet</div>
            <div className="text-2xs text-text-muted">WhatsApp message to each new lead</div>
          </div>
        </div>

        <div className="min-w-[12rem] flex-1 @lg:max-w-[18rem]">
          <label htmlFor={selectId} className="mb-1 block text-2xs font-medium text-text-muted">Greeting template</label>
          <Select
            id={selectId}
            className="disabled:cursor-not-allowed disabled:opacity-50"
            value={savedUid}
            onChange={handleTemplateChange}
            disabled={saving || !form.autoGreetEnabled}
          >
            <option value="">— None —</option>
            {savedMissing && <option value={savedUid}>Saved template (not in the approved list)</option>}
            {approvedTemplates.map((t) => (
              <option key={t.templateUid} value={t.templateUid}>
                {t.name} ({t.language})
              </option>
            ))}
          </Select>
        </div>

        {form.autoGreetEnabled && savedUid === '' && (
          <p className="basis-full text-2xs leading-relaxed text-warning">
            {approvedTemplates.length === 0 ? (
              <>No approved templates yet, so nothing is sent. Create one in <Link to="/templates" className="font-semibold underline">Templates</Link>.</>
            ) : (
              'Pick a template. No greeting is sent until you do.'
            )}
          </p>
        )}
      </div>
    </li>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function LeadAdFormsSettings() {
  const [forms, setForms] = useState<LeadAdForm[]>([]);
  const [approvedTemplates, setApprovedTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const { active: activeAccount } = useActiveAccount();

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    Promise.all([
      leadAdFormsService.list(),
      templatesService.list({ status: 'APPROVED', limit: 100, accountUid: activeAccount?.accountUid }),
    ])
      .then(([formList, templateList]) => {
        if (!alive) return;
        setForms(formList);
        setApprovedTemplates(templateList.items);
      })
      .catch(() => { if (alive) setLoadError(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [activeAccount?.accountUid, reloadKey]);

  const retry = () => {
    setLoading(true);
    setReloadKey((k) => k + 1);
  };

  const handleUpdated = (updated: LeadAdForm) => {
    setForms((prev) => prev.map((f) => (f.formUid === updated.formUid ? updated : f)));
  };

  return (
    <SettingsPage
      eyebrow="Channels"
      title="Lead ad forms"
      description="Meta instant forms that send leads into Slightor. Turn on auto-greet to message each new lead on WhatsApp straight away."
    >
      <SettingsSection
        title="Your forms"
        description="A form shows up here after its first lead. Auto-greet sends the lead one of your approved WhatsApp templates."
      >
        <SettingsCard padded={false}>
          {loadError ? (
            <LoadErrorState bare what="your lead ad forms" onRetry={retry} />
          ) : loading ? (
            <div className="space-y-2 p-5">{[0, 1].map((i) => <div key={i} className="skeleton h-24 rounded-xl" />)}</div>
          ) : forms.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary text-white shadow-soft"><ClipboardList size={20} /></div>
              <div className="text-sm font-medium text-text-primary">No forms yet</div>
              <p className="max-w-sm text-xs text-text-muted">Forms appear here automatically when the first lead comes in from a Meta lead ad. Follow the steps below to set that up.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {forms.map((form) => (
                <FormRow
                  key={form.formUid}
                  form={form}
                  approvedTemplates={approvedTemplates}
                  onUpdated={handleUpdated}
                />
              ))}
            </ul>
          )}
        </SettingsCard>
      </SettingsSection>

      {/* The old steps (Meta App Dashboard, server env verify token) are platform-operator
          work a tenant can't do; the tenant's part is the Page subscription. */}
      <SettingsSection
        title="Receiving Meta lead ads"
        description="What to set up so leads from your Meta lead ads reach Slightor."
      >
        <SettingsCard>
          <ol className="space-y-4">
            <Step n={1}>
              Connect Facebook in{' '}
              <Link to="/settings/integrations" className="font-semibold text-primary hover:underline">Integrations</Link>
              {' '}with the Page that runs your lead ads.
            </Step>
            <Step n={2}>
              Open{' '}
              <Link to="/facebook/lead-forms" className="font-semibold text-primary hover:underline">Facebook Ads → Lead Forms</Link>
              {' '}and check that the Page subscription card shows lead delivery as active. Repair it there if not.
            </Step>
            <Step n={3}>
              Forms appear here automatically the first time a lead is submitted. Turn on auto-greet and pick an approved utility template to send an instant WhatsApp greeting.
            </Step>
          </ol>
        </SettingsCard>
      </SettingsSection>
    </SettingsPage>
  );
}
